import { Modal } from "bootstrap";
import $ from "modules/jquery_global";
import { focusFirstInteractive } from "./modal_focus";

const MODAL_SELECTOR = ".modal";
const OPEN_MODAL_SELECTOR = ".modal.show";
const MANAGED_TRIGGER_SELECTOR = "[data-courseware-modal='true']";
const MODAL_TARGET_ATTRIBUTE = "data-courseware-modal-target";
const RETURN_FOCUS_ATTRIBUTE = "data-modal-return-focus";
const DISMISSIBLE_ATTRIBUTE = "data-modal-dismissible";
const EVENT_NAMESPACE = ".coursewareModal";

const modalState = new WeakMap();
const shownModals = new WeakSet();
let pendingTriggerState = null;
let pendingChainState = null;

const resolveElement = (target) => {
    if (!target) return null;
    if (target.jquery) return target[0] || null;
    if (typeof target === "string") return document.querySelector(target);
    return target instanceof Element ? target : null;
};

const isVisible = (element) => {
    return element instanceof HTMLElement &&
        element.isConnected &&
        (element.offsetParent !== null || element.getClientRects().length > 0);
};

const dropdownToggleFor = (trigger) => {
    const menu = trigger instanceof HTMLElement ? trigger.closest(".dropdown-menu") : null;
    return menu?.parentElement?.querySelector('[data-bs-toggle="dropdown"]') || null;
};

const buildState = ({ trigger, returnFocus } = {}) => {
    const triggerElement = resolveElement(trigger);
    const declaredReturnFocus = triggerElement?.getAttribute(RETURN_FOCUS_ATTRIBUTE);
    const modalTarget = triggerElement?.getAttribute(MODAL_TARGET_ATTRIBUTE) ||
        triggerElement?.getAttribute("data-bs-target");

    return {
        trigger: triggerElement,
        returnFocus: returnFocus || declaredReturnFocus || dropdownToggleFor(triggerElement) || triggerElement,
        modalTarget
    };
};

const stateAppliesToModal = (state, modal) => {
    return !state?.modalTarget || resolveElement(state.modalTarget) === modal;
};

const stateForShow = (modal, { trigger, returnFocus } = {}) => {
    const existingState = modalState.get(modal);
    if (existingState) return existingState;

    if (trigger || returnFocus) {
        const state = buildState({ trigger, returnFocus });
        return stateAppliesToModal(state, modal) ? state : null;
    }
    if (pendingChainState && pendingChainState.modal !== modal) return pendingChainState.state;
    return stateAppliesToModal(pendingTriggerState, modal) ? pendingTriggerState : null;
};

const captureState = (modal, options = {}) => {
    const state = stateForShow(modal, options);
    if (!state) return null;

    modalState.set(modal, state);
    pendingTriggerState = null;
    pendingChainState = null;
    return state;
};

const modalHasUsableFocus = (modal) => {
    const activeElement = document.activeElement;
    return activeElement instanceof HTMLElement &&
        modal.contains(activeElement) &&
        isVisible(activeElement) &&
        !activeElement.matches(":disabled, [aria-disabled='true']");
};

const ensureFocus = (target) => {
    const modal = resolveElement(target);
    if (!modal || !modalState.has(modal)) return;

    window.requestAnimationFrame(() => {
        if (shownModals.has(modal) && modal.matches(OPEN_MODAL_SELECTOR) && !modalHasUsableFocus(modal)) {
            focusFirstInteractive(modal);
        }
    });
};

const validExternalFocusExists = (modal) => {
    const activeElement = document.activeElement;
    return activeElement instanceof HTMLElement &&
        activeElement !== document.body &&
        !modal.contains(activeElement) &&
        isVisible(activeElement);
};

const restoreFocus = (modal, state) => {
    if (!state || document.querySelector(OPEN_MODAL_SELECTOR) || validExternalFocusExists(modal)) return;

    const declaredTarget = resolveElement(state.returnFocus);
    const target = isVisible(declaredTarget) ? declaredTarget : state.trigger;
    if (isVisible(target)) target.focus();
};

const handleShow = (event) => {
    const modal = event.target;
    if (!(modal instanceof Element) || !modal.matches(MODAL_SELECTOR) || event.defaultPrevented) return;

    const relatedTarget = resolveElement(event.relatedTarget);
    const managedTrigger = relatedTarget?.matches(MANAGED_TRIGGER_SELECTOR) ? relatedTarget : null;
    const state = captureState(modal, { trigger: managedTrigger });
    if (!state) return;

    shownModals.delete(modal);
};

const handleShown = (event) => {
    const modal = event.target;
    if (!(modal instanceof Element) || !modal.matches(MODAL_SELECTOR) || !modalState.has(modal)) return;

    shownModals.add(modal);
    focusFirstInteractive(modal);
};

const handleHide = (event) => {
    const modal = event.target;
    if (!(modal instanceof Element) || !modal.matches(MODAL_SELECTOR) || !modalState.has(modal)) return;

    if (modal.getAttribute(DISMISSIBLE_ATTRIBUTE) === "false") {
        event.preventDefault();
        return;
    }
    if (event.defaultPrevented) return;

    shownModals.delete(modal);
    const state = modalState.get(modal);
    pendingChainState = state ? { modal, state } : null;
};

const handleHidden = (event) => {
    const modal = event.target;
    if (!(modal instanceof Element) || !modal.matches(MODAL_SELECTOR) || !modalState.has(modal)) return;

    shownModals.delete(modal);
    const state = modalState.get(modal);
    modalState.delete(modal);

    window.requestAnimationFrame(() => {
        restoreFocus(modal, state);
        if (pendingChainState?.modal === modal) pendingChainState = null;
    });
};

const rememberTrigger = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest(MANAGED_TRIGGER_SELECTOR) : null;
    if (!trigger) return;

    const state = buildState({ trigger });
    pendingTriggerState = state;
    window.setTimeout(() => {
        if (pendingTriggerState === state) pendingTriggerState = null;
    }, 30000);
};

const show = (target, options = {}) => {
    const modal = resolveElement(target);
    if (!modal?.matches(MODAL_SELECTOR)) return null;

    const { trigger, returnFocus, ...bootstrapOptions } = options;
    const state = captureState(modal, { trigger, returnFocus });

    // Shared endpoints may serve both adopted and legacy modal paths. Returning
    // null lets those callers retain their existing Bootstrap behavior until
    // their trigger explicitly opts into this lifecycle.
    if (!state) return null;

    const config = { keyboard: true, ...bootstrapOptions };
    let instance = Modal.getInstance(modal);
    if (instance && !modal.classList.contains("show")) {
        instance.dispose();
        instance = null;
    }
    instance ||= new Modal(modal, config);
    instance.show(state.trigger || null);
    ensureFocus(modal);
    return instance;
};

const setDismissible = (target, dismissible) => {
    const modal = resolveElement(target);
    if (!modal?.matches(MODAL_SELECTOR)) return;

    modal.setAttribute(DISMISSIBLE_ATTRIBUTE, dismissible ? "true" : "false");
    if (dismissible) {
        modal.removeAttribute("aria-busy");
        ensureFocus(modal);
    } else {
        modal.setAttribute("aria-busy", "true");
    }
};

const initialize = () => {
    [
        ["show.bs.modal", handleShow],
        ["shown.bs.modal", handleShown],
        ["hide.bs.modal", handleHide],
        ["hidden.bs.modal", handleHidden]
    ].forEach(([eventName, handler]) => {
        document.removeEventListener(eventName, handler, true);
        document.addEventListener(eventName, handler, true);
    });

    document.removeEventListener("click", rememberTrigger, true);
    document.addEventListener("click", rememberTrigger, true);

    $(document)
        .off(`click${EVENT_NAMESPACE}`, MODAL_SELECTOR)
        .on(`click${EVENT_NAMESPACE}`, MODAL_SELECTOR, function () {
            if (modalState.has(this)) ensureFocus(this);
        })
        .off(`ajax:complete${EVENT_NAMESPACE}`, MANAGED_TRIGGER_SELECTOR)
        .on(`ajax:complete${EVENT_NAMESPACE}`, MANAGED_TRIGGER_SELECTOR, function () {
            if (pendingTriggerState?.trigger === this) pendingTriggerState = null;
        });
};

initialize();

window.CoursewareModal = { ensureFocus, setDismissible, show };

export { ensureFocus, initialize, setDismissible, show };
